import { userDataService, UserPreferences } from '../services/userDataService';

export class PreferencesManager {
  private static instance: PreferencesManager;
  private userId: string | null = null;
  private preferences: UserPreferences | null = null;
  private loadFailed = false;
  private listeners: Array<(prefs: UserPreferences) => void> = [];

  private constructor() {}

  static getInstance(): PreferencesManager {
    if (!PreferencesManager.instance) {
      PreferencesManager.instance = new PreferencesManager();
    }
    return PreferencesManager.instance;
  }

  setUserId(userId: string) {
    this.userId = userId;
  }

  getUserId(): string | null {
    return this.userId;
  }

  /**
   * Get or create a demo user ID for demo mode
   * Returns a persistent demo user ID stored in localStorage
   */
  getDemoUserId(): string {
    const DEMO_USER_ID_KEY = 'demo_user_id';

    // Check if we already have a demo user ID
    let demoUserId = localStorage.getItem(DEMO_USER_ID_KEY);

    if (!demoUserId) {
      // Generate a new demo user ID: demo-{timestamp}-{random}
      const timestamp = Date.now();
      const random = Math.random().toString(36).substring(2, 8);
      demoUserId = `demo-${timestamp}-${random}`;

      // Store it for future use
      localStorage.setItem(DEMO_USER_ID_KEY, demoUserId);
    }

    return demoUserId;
  }

  /**
   * Initialize demo mode - sets up a persistent demo user ID
   */
  initializeDemoMode(): void {
    const demoUserId = this.getDemoUserId();
    this.setUserId(demoUserId);
  }

  async loadPreferences(): Promise<UserPreferences> {
    if (!this.userId) {
      throw new Error('User ID not set');
    }

    try {
      this.preferences = await userDataService.getPreferences(this.userId);
      this.loadFailed = false;
      this.notifyListeners();
      return this.preferences;
    } catch (error) {
      console.error('Failed to load preferences:', error);
      // Fall back to defaults so the UI still renders, but flag that these are
      // placeholders rather than the account's real settings. Callers must not
      // treat an all-empty payload from a failed fetch as "this user has no saved
      // preferences" — doing so overwrites the server copy with local defaults.
      this.loadFailed = true;
      this.preferences = {
        userId: this.userId,
        preferredUnit: 'imperial',
        locationDisplayPreference: 'geotagged',
        updatedAt: new Date().toISOString(),
      };
      return this.preferences;
    }
  }

  /**
   * True when the last loadPreferences() call fell back to defaults because the
   * fetch failed. Guards destructive "seed the new user" writes.
   */
  didLoadFail(): boolean {
    return this.loadFailed;
  }

  async savePreferences(updates: Partial<UserPreferences>): Promise<void> {
    if (!this.userId) {
      throw new Error('User ID not set');
    }

    if (!this.preferences) {
      await this.loadPreferences();
    }

    // Merge customSettings rather than replacing it. Callers build their payload by
    // spreading the cached customSettings, which is empty if the account's prefs were
    // never loaded — taking that object wholesale would drop every key the caller
    // didn't know about (certifications, privacy, notification flags). Merging here
    // makes a partial save safe no matter what the caller passed.
    const mergedCustomSettings =
      updates.customSettings || this.preferences?.customSettings
        ? {
            ...this.preferences?.customSettings,
            ...updates.customSettings,
          }
        : undefined;

    this.preferences = {
      ...this.preferences!,
      ...updates,
      ...(mergedCustomSettings ? { customSettings: mergedCustomSettings } : {}),
      userId: this.userId,
      updatedAt: new Date().toISOString(),
    };

    try {
      await userDataService.savePreferences(this.preferences);
      this.notifyListeners();
    } catch (error) {
      console.error('Failed to save preferences:', error);
      throw error;
    }
  }

  getPreferences(): UserPreferences | null {
    return this.preferences;
  }

  subscribe(listener: (prefs: UserPreferences) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private notifyListeners() {
    if (this.preferences) {
      this.listeners.forEach(listener => listener(this.preferences!));
    }
  }
}

export const preferencesManager = PreferencesManager.getInstance();
